// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutoSave } from '../../hooks/useAutoSave';
import { ActivityIndicator, Pressable, StyleSheet, TextInput, View } from 'react-native';
import * as Location from 'expo-location';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import SectionHeading from '../shared/SectionHeading';
import { t } from '@/i18n';
import { formatLastPlayed } from '@/hooks/useRecentCourses';
import type { Course, RecentCourse, TourRoundSummary } from '../../types';

type Props = {
  round: TourRoundSummary;
  courses: Course[];
  recentCourses?: RecentCourse[];
  loadingCourses: boolean;
  courseError: string | null;
  onFetchCourses: (coords: { lat: number; lon: number }) => void;
  onUpdateRoundSettings: (roundId: string, updates: { name?: string; playDate?: string; courseName?: string; course?: Course; holesCount?: number }) => void;
};

export default function RoundSettingsEditor({
  round,
  courses,
  recentCourses = [],
  loadingCourses,
  courseError,
  onFetchCourses,
  onUpdateRoundSettings,
}: Props) {
  // --- Title ---
  const [titleValue, setTitleValue] = useState(round.name ?? '');

  // --- Date ---
  const [dateValue, setDateValue] = useState(round.playDate ?? '');

  // --- Course search combo ---
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [courseInput, setCourseInput] = useState(round.courseName ?? '');
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const courseInitializedRef = useRef(false);

  // Sync when switching round OR when an external update arrives (e.g. round.updated via WS)
  const lastSavedTitleRef = useRef(round.name ?? '');
  const lastSavedDateRef = useRef(round.playDate ?? '');
  const prevRoundIdRef = useRef(round.id);
  useEffect(() => {
    const isNewRound = prevRoundIdRef.current !== round.id;
    if (isNewRound) {
      prevRoundIdRef.current = round.id;
      setTitleValue(round.name ?? '');
      setDateValue(round.playDate ?? '');
      setCourseInput(round.courseName ?? '');
      setSelectedCourseId(null);
      courseInitializedRef.current = false;
      lastSavedTitleRef.current = round.name ?? '';
      lastSavedDateRef.current = round.playDate ?? '';
      setHolesCount(round.holesCount ?? 18);
      return;
    }
    // External update: prop changed but not from our own auto-save
    const incomingTitle = round.name ?? '';
    if (incomingTitle !== lastSavedTitleRef.current) {
      setTitleValue(incomingTitle);
      lastSavedTitleRef.current = incomingTitle;
    }
    const incomingDate = round.playDate ?? '';
    if (incomingDate !== lastSavedDateRef.current) {
      setDateValue(incomingDate);
      lastSavedDateRef.current = incomingDate;
    }
    if (round.courseName) {
      setCourseInput(round.courseName);
    }
    if (round.holesCount) {
      setHolesCount(round.holesCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.id, round.name, round.playDate, round.courseName, round.holesCount]);

  // Auto-save title
  useAutoSave(titleValue, lastSavedTitleRef.current, (value) => {
    lastSavedTitleRef.current = value;
    onUpdateRoundSettings(round.id, { name: value });
  });

  // Auto-save date
  useAutoSave(dateValue, lastSavedDateRef.current, (value) => {
    lastSavedDateRef.current = value;
    onUpdateRoundSettings(round.id, { playDate: value });
  });

  // Location fetch
  useEffect(() => {
    if (locationRequested || coords) return;
    setLocationRequested(true);
    const requestLocation = async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          setCoords({ lat: 57.7089, lon: 11.9746 });
          return;
        }
        const position = await Location.getCurrentPositionAsync({});
        setCoords({ lat: position.coords.latitude, lon: position.coords.longitude });
      } catch {
        setCoords({ lat: 57.7089, lon: 11.9746 });
      }
    };
    requestLocation();
  }, [coords, locationRequested]);

  useEffect(() => {
    if (coords && courses.length === 0 && !loadingCourses) {
      onFetchCourses(coords);
    }
  }, [coords, courses.length, loadingCourses, onFetchCourses]);

  // Auto-select matching course when courses load
  const selectedCourse = courses.find((c) => c.id === selectedCourseId) ?? null;
  useEffect(() => {
    if (courseInitializedRef.current || !round.courseName || courses.length === 0) return;
    courseInitializedRef.current = true;
    const match = courses.find((c) => c.name === round.courseName);
    if (match) {
      setSelectedCourseId(match.id);
      setCourseInput(match.name);
    }
  }, [courses, round.courseName]);

  // Keep input in sync with selected course when dropdown is closed
  useEffect(() => {
    if (selectedCourse && !courseDropdownOpen) {
      setCourseInput(selectedCourse.name);
    }
  }, [selectedCourse, courseDropdownOpen]);

  const filteredCourses = useMemo(() => {
    const query = courseInput.trim().toLowerCase();
    if (!query) return courses.slice(0, 8);
    return courses
      .filter((c) => c.name.toLowerCase().includes(query) || c.address.toLowerCase().includes(query))
      .slice(0, 8);
  }, [courses, courseInput]);

  const showRecents = courseInput.trim().length === 0 && recentCourses.length > 0;
  const showCourseDropdown = courseDropdownOpen && (filteredCourses.length > 0 || showRecents);

  useEffect(() => {
    setHighlightIndex(0);
  }, [filteredCourses.length, courseInput]);

  // --- Holes count ---
  const [holesCount, setHolesCount] = useState<number>(round.holesCount ?? 18);

  const handleHolesChange = useCallback((value: 9 | 18) => {
    setHolesCount(value);
    onUpdateRoundSettings(round.id, { holesCount: value });
  }, [onUpdateRoundSettings, round.id]);

  const selectCourse = useCallback((course: Course) => {
    setSelectedCourseId(course.id);
    setCourseInput(course.name);
    setCourseDropdownOpen(false);
    setHighlightIndex(0);
    // Send full course object + courseName for draft round tracking
    onUpdateRoundSettings(round.id, { courseName: course.name, course });
  }, [onUpdateRoundSettings, round.id]);

  const selectRecentCourse = useCallback((recent: RecentCourse) => {
    // Recently-played courses carry no coordinates; the round already references
    // the course by id/name, so coords are not needed for the settings update.
    selectCourse({ id: recent.id, name: recent.name, address: recent.address, lat: 0, lon: 0 });
  }, [selectCourse]);

  const handleCourseKeyDown = useCallback(
    (e: any) => {
      const key = e.key ?? e.nativeEvent?.key;
      if (!showCourseDropdown) {
        if (key === 'ArrowDown' || key === 'Enter') setCourseDropdownOpen(true);
        return;
      }
      if (key === 'ArrowDown') {
        e.preventDefault?.();
        setHighlightIndex((prev) => Math.min(prev + 1, filteredCourses.length - 1));
      } else if (key === 'ArrowUp') {
        e.preventDefault?.();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (key === 'Enter') {
        e.preventDefault?.();
        const target = filteredCourses[highlightIndex];
        if (target) selectCourse(target);
      } else if (key === 'Escape') {
        setCourseDropdownOpen(false);
      }
    },
    [filteredCourses, highlightIndex, selectCourse, showCourseDropdown]
  );

  return (
    <View style={styles.container}>
      <SectionHeading title="Round Settings" />

      {/* Round Title */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Title (optional)</Text>
        <TextInput
          style={styles.textInput}
          value={titleValue}
          onChangeText={setTitleValue}
          placeholder="e.g. Saturday Morning Round"
          placeholderTextColor={appColors.text.muted}
        />
      </View>

      {/* Play Date */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Play Date</Text>
        <input
          type="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          style={dateInputStyle}
        />
      </View>

      {/* Course / Location */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Course</Text>

        {loadingCourses && (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color={appColors.accent.primary} />
            <Text style={styles.loadingText}>Loading nearby courses...</Text>
          </View>
        )}

        {courseError && <Text style={styles.errorText}>{courseError}</Text>}

        {!loadingCourses && courses.length > 0 && (
          <View style={styles.comboWrapper}>
            <View style={[styles.inputRow, showCourseDropdown && styles.inputRowOpen]}>
              <TextInput
                style={styles.comboInput}
                value={courseInput}
                onChangeText={(value) => {
                  setCourseInput(value);
                  setCourseDropdownOpen(true);
                  if (selectedCourseId) setSelectedCourseId(null);
                }}
                placeholder="Search for a course..."
                placeholderTextColor={appColors.text.muted}
                onFocus={() => setCourseDropdownOpen(true)}
                onBlur={() => {
                  setTimeout(() => setCourseDropdownOpen(false), 200);
                }}
                onKeyPress={handleCourseKeyDown}
              />
              {selectedCourse && !courseDropdownOpen && (
                <Text style={styles.selectedBadge}>Selected</Text>
              )}
            </View>

            {showCourseDropdown && (
              <View style={styles.dropdown}>
                {showRecents && (
                  <View>
                    <Text style={styles.recentHeader}>
                      {t('players.recentlyPlayedTitle')}
                      <Text style={styles.recentHeaderSub}> · {t('players.recentlyPlayedSubtitle')}</Text>
                    </Text>
                    {recentCourses.map((recent, index) => {
                      const isSelected = selectedCourseId === recent.id;
                      return (
                        <Pressable
                          key={recent.lastRoundId || recent.id}
                          style={[
                            styles.dropdownItem,
                            index === 0 && styles.dropdownItemFirst,
                            isSelected && styles.dropdownItemSelected,
                          ]}
                          onPress={() => selectRecentCourse(recent)}
                        >
                          <View style={styles.courseInfo}>
                            <Text style={[styles.courseName, isSelected && styles.courseNameActive]}>{recent.name}</Text>
                            {recent.address ? <Text style={styles.courseAddress} numberOfLines={1}>{recent.address}</Text> : null}
                          </View>
                          {formatLastPlayed(recent) ? (
                            <Text style={styles.recentMeta}>{formatLastPlayed(recent)}</Text>
                          ) : null}
                        </Pressable>
                      );
                    })}
                    <Text style={styles.recentDivider}>{t('players.lookingForAnother')}</Text>
                  </View>
                )}
                {filteredCourses.map((course, index) => {
                  const isHighlighted = index === highlightIndex;
                  const isSelected = selectedCourseId === course.id;
                  return (
                    <Pressable
                      key={course.id}
                      style={[
                        styles.dropdownItem,
                        index === 0 && styles.dropdownItemFirst,
                        isHighlighted && styles.dropdownItemHighlighted,
                        isSelected && styles.dropdownItemSelected,
                      ]}
                      onPress={() => selectCourse(course)}
                      onHoverIn={() => setHighlightIndex(index)}
                    >
                      <View style={styles.courseInfo}>
                        <Text style={[styles.courseName, (isHighlighted || isSelected) && styles.courseNameActive]}>
                          {course.name}
                        </Text>
                        <Text style={styles.courseAddress} numberOfLines={1}>{course.address}</Text>
                      </View>
                      {isSelected && <Text style={styles.checkmark}>✓</Text>}
                    </Pressable>
                  );
                })}
              </View>
            )}
          </View>
        )}

        {selectedCourse && !courseDropdownOpen && (
          <View style={styles.detailBox}>
            <Text style={styles.detailAddress}>{selectedCourse.address}</Text>
          </View>
        )}
      </View>

      {/* Holes Count */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Number of Holes</Text>
        <View style={styles.holesRow}>
          <Pressable
            style={[styles.holesOption, holesCount === 9 && styles.holesOptionSelected]}
            onPress={() => handleHolesChange(9)}
          >
            <Text style={[styles.holesOptionText, holesCount === 9 && styles.holesOptionTextSelected]}>9 holes</Text>
          </Pressable>
          <Pressable
            style={[styles.holesOption, holesCount === 18 && styles.holesOptionSelected]}
            onPress={() => handleHolesChange(18)}
          >
            <Text style={[styles.holesOptionText, holesCount === 18 && styles.holesOptionTextSelected]}>18 holes</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const dateInputStyle: React.CSSProperties = {
  border: `1px solid ${appColors.border.default}`,
  borderRadius: 10,
  backgroundColor: appColors.surface.input,
  paddingInline: 14,
  paddingBlock: 10,
  fontSize: 14,
  fontWeight: 600,
  color: appColors.text.primary,
  fontFamily: '-apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  colorScheme: 'dark',
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 2,
  },
  textInput: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 10,
    backgroundColor: appColors.surface.input,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  loadingText: {
    fontSize: 13,
    color: appColors.text.muted,
  },
  errorText: {
    fontSize: 13,
    color: appColors.status.danger,
  },
  comboWrapper: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 10,
    backgroundColor: appColors.surface.input,
    overflow: 'hidden',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 10,
  },
  inputRowOpen: {
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  comboInput: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  selectedBadge: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.accent.primary,
    backgroundColor: appColors.accent.subtleBg,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
    overflow: 'hidden',
  },
  dropdown: {
    backgroundColor: appColors.surface.elevated,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
  },
  dropdownItemFirst: {
    borderTopWidth: 0,
  },
  dropdownItemHighlighted: {
    backgroundColor: appColors.accent.subtleBg,
  },
  dropdownItemSelected: {
    backgroundColor: appColors.accent.subtleBg,
  },
  courseInfo: {
    flex: 1,
    gap: 2,
  },
  courseName: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  courseNameActive: {
    fontWeight: '700',
  },
  courseAddress: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  checkmark: {
    fontSize: 14,
    fontWeight: '800',
    color: appColors.accent.primary,
    marginLeft: 8,
  },
  recentHeader: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.accent.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 4,
  },
  recentHeaderSub: {
    fontWeight: '400',
    color: appColors.text.muted,
    textTransform: 'none',
    letterSpacing: 0,
  },
  recentMeta: {
    fontSize: 11,
    color: appColors.text.muted,
    marginLeft: 8,
  },
  recentDivider: {
    fontSize: 11,
    color: appColors.text.muted,
    paddingHorizontal: 14,
    paddingTop: 8,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
  },
  detailBox: {
    backgroundColor: appColors.surface.alt,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  detailAddress: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  holesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  holesOption: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.input,
    alignItems: 'center',
  },
  holesOptionSelected: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.subtleBg,
  },
  holesOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.muted,
  },
  holesOptionTextSelected: {
    color: appColors.accent.primary,
    fontWeight: '700',
  },
});
